<script lang="ts">
  import type { MeterResult } from '../../lib/complexity';
  import type { SandboxClient } from '../../lib/sandboxd';
  import { sandbox } from '../../services';
  import CodeBlock from '../CodeBlock.svelte';
  import { isSubmitChord } from '../keyboard';
  import Tabs from '../Tabs.svelte';
  import type { ResultSeries } from './chart-model';
  import {
    deviation,
    EXPERIMENTS,
    experimentById,
    variantMeter,
    type DeviationEntry,
    type ExperimentId,
    type Variant,
  } from './experiments';
  import MeterResults from './MeterResults.svelte';
  import { outputAgreement } from './output-check';
  import { runMeter } from './run-meter';
  import Pow from './Pow.svelte';
  import StateIcon from './StateIcon.svelte';

  /**
   * 比較実験（docs/curriculum/labs.md「最初の 3 つの比較実験」）。
   * 予想を選ぶ → 遅い書き方を実行 → 予想と実測を比べる → 速い書き方を実行して並べる。
   * 速い書き方のコードは、遅い書き方を測るまで出さない。
   */
  interface Props {
    client?: Pick<SandboxClient, 'run'>;
    /** ずれの記録が増えたとき（保存先があれば親が受け取る）。 */
    ondeviation?: ((entry: DeviationEntry) => void) | undefined;
  }

  let { client = sandbox, ondeviation }: Props = $props();
  const uid = $props.id();

  interface Measured {
    result: MeterResult;
    stdouts: string[];
  }
  interface ExpState {
    declared: string | null;
    slow: Measured | null;
    fast: Measured | null;
    compileError: string | null;
  }

  const fresh = (): ExpState => ({ declared: null, slow: null, fast: null, compileError: null });
  let states = $state<Record<ExperimentId, ExpState>>({
    duplicate: fresh(),
    search: fresh(),
    'range-sum': fresh(),
  });
  let selected = $state<ExperimentId>('duplicate');
  let running = $state<Variant | null>(null);
  let notice = $state<string | null>(null);
  let error = $state<{ message: string; retryable: boolean; variant: Variant } | null>(null);
  let log = $state<DeviationEntry[]>([]);
  let controller: AbortController | null = null;

  $effect(() => () => controller?.abort());

  const exp = $derived(experimentById(selected));
  const st = $derived(states[selected]);
  const dev = $derived(
    st.slow && st.declared ? deviation(exp, st.declared, st.slow.result.points) : null,
  );
  const agreement = $derived(
    st.slow && st.fast
      ? outputAgreement(
          st.slow.result.points,
          st.slow.stdouts,
          st.fast.result.points,
          st.fast.stdouts,
        )
      : null,
  );

  const series = $derived.by((): ResultSeries[] => {
    const out: ResultSeries[] = [];
    if (st.slow) {
      out.push({
        key: 1,
        label: exp.slow.label,
        points: st.slow.result.points,
        skipped: st.slow.result.skipped,
        ops: exp.slow.ops,
      });
    }
    if (st.fast) {
      out.push({
        key: 2,
        label: exp.fast.label,
        points: st.fast.result.points,
        skipped: st.fast.result.skipped,
        ops: exp.fast.ops,
      });
    }
    return out;
  });

  function select(id: ExperimentId) {
    if (running) return;
    selected = id;
    error = null;
  }

  async function measure(variant: Variant) {
    if (running) return;
    const e = exp;
    const s = states[e.id];
    if (variant === 'slow' && s.declared === null) return;
    if (variant === 'fast' && s.slow === null) return;
    const def = e[variant];
    const again = s[variant] !== null;
    running = variant;
    error = null;
    notice = null;
    controller = new AbortController();
    const out = await runMeter(client, {
      code: def.code,
      meter: variantMeter(def),
      purpose: 'lab',
      span: { mode: 'compare', experiment: e.id, variant },
      bypassCache: again,
      signal: controller.signal,
      onRetry: (t) => (notice = t),
    });
    running = null;
    notice = null;
    controller = null;
    switch (out.kind) {
      case 'ok': {
        s[variant] = { result: out.result, stdouts: out.stdouts };
        s.compileError = null;
        if (variant === 'slow' && s.declared !== null) {
          const d = deviation(e, s.declared, out.result.points);
          if (d) {
            log = [d, ...log.filter((x) => x.experimentId !== d.experimentId)];
            ondeviation?.(d);
          }
        }
        break;
      }
      case 'compile-error':
        s.compileError = out.output;
        break;
      case 'error':
        error = { message: out.message, retryable: out.retryable, variant };
        break;
      case 'nothing-to-send':
        error = { message: '送れる N がありませんでした。', retryable: false, variant };
        break;
      case 'aborted':
        break;
    }
  }

  function onkeydown(e: KeyboardEvent) {
    const t = e.target;
    const typing =
      t instanceof HTMLTextAreaElement ||
      t instanceof HTMLSelectElement ||
      (t instanceof HTMLInputElement && t.type !== 'radio');
    const digit = /^[1-9]$/.test(e.key) ? Number(e.key) - 1 : -1;
    if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey && digit >= 0) {
      const c = exp.choices[digit];
      if (c && st.slow === null && running === null) {
        e.preventDefault();
        states[selected].declared = c.id;
      }
      return;
    }
    if (!isSubmitChord(e)) return;
    e.preventDefault();
    if (st.slow === null) void measure('slow');
    else void measure('fast');
  }

  const variantName: Record<Variant, string> = { slow: '遅い書き方', fast: '速い書き方' };
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="compare" {onkeydown}>
  <Tabs
    label="比較実験"
    items={EXPERIMENTS.map((e, i) => ({ id: e.id, label: e.title, n: i + 1 }))}
    {selected}
    panelId="{uid}-panel"
    idPrefix="{uid}-tab"
    onselect={select}
  />

  <div id="{uid}-panel" role="tabpanel" aria-labelledby="{uid}-tab-{selected}" class="panel-body">
    <p>{exp.task}</p>

    <section class="step" aria-labelledby="{uid}-s1">
      <h3 id="{uid}-s1"><span class="step-n">1</span>先に予想する</h3>
      <fieldset disabled={st.slow !== null || running !== null}>
        <legend><Pow text={exp.question} /></legend>
        <div class="choices">
          {#each exp.choices as c, i (c.id)}
            <label class="choice">
              <input
                type="radio"
                name="{uid}-{exp.id}-declare"
                value={c.id}
                checked={st.declared === c.id}
                onchange={() => (states[selected].declared = c.id)}
              />
              <kbd aria-hidden="true">{i + 1}</kbd><Pow text={c.label} />
            </label>
          {/each}
        </div>
      </fieldset>
      {#if st.slow !== null}
        <p class="text-soft small">予想は、計測を始めると変えられません。</p>
      {/if}
    </section>

    <section class="step" aria-labelledby="{uid}-s2">
      <h3 id="{uid}-s2"><span class="step-n">2</span>遅い書き方（{exp.slow.label}）を測る</h3>
      <div class="code-scroll"><CodeBlock code={exp.slow.code} label="遅い書き方のコード" /></div>
      <div class="actions">
        <button
          type="button"
          class="btn btn-primary"
          disabled={st.declared === null || running !== null}
          aria-busy={running === 'slow'}
          data-testid="measure-slow"
          onclick={() => measure('slow')}
        >
          {#if running === 'slow'}計測中{:else}{st.slow
              ? '遅い書き方をもう一度測る'
              : '遅い書き方を測る'}
            {#if st.slow === null}<kbd>⌘ Enter</kbd>{/if}{/if}
        </button>
        {#if st.declared === null}
          <span class="text-soft small">先に 1 で予想を選んでください。</span>
        {/if}
      </div>
    </section>

    {#if notice}
      <p class="status small" role="status">{notice}</p>
    {/if}
    {#if error}
      <div class="status err small" role="alert">
        <StateIcon kind="over" /><span>{error.message}</span>
        {#if error.retryable && error.variant}
          {@const v = error.variant}
          <button type="button" class="btn btn-sm" onclick={() => measure(v)}>再実行</button>
        {/if}
      </div>
    {/if}
    {#if st.compileError !== null}
      <div class="status err small" role="alert">
        <StateIcon kind="over" /><span>コンパイルできませんでした。</span>
      </div>
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <pre class="diag" tabindex="0">{st.compileError}</pre>
    {/if}

    {#if dev}
      <section class="step" aria-labelledby="{uid}-s3">
        <h3 id="{uid}-s3"><span class="step-n">3</span>予想と実測を比べる</h3>
        <p class="dev {dev.match ? 'match' : 'gap'}" data-testid="deviation">
          <StateIcon kind={dev.match ? 'ok' : 'diff'} />
          <span>
            {#if dev.match}
              予想「<Pow text={dev.declared.label} />」と実測「<Pow
                text={dev.measured.label}
              />」が一致しました。
            {:else}
              予想は「<Pow text={dev.declared.label} />」でしたが、実測は「<Pow
                text={dev.measured.label}
              />」でした。
            {/if}
          </span>
        </p>
        <p class="small num">{dev.detail}</p>
      </section>
    {/if}

    {#if st.slow}
      <section class="step" aria-labelledby="{uid}-s4">
        <h3 id="{uid}-s4"><span class="step-n">4</span>速い書き方（{exp.fast.label}）を測る</h3>
        <div class="code-scroll"><CodeBlock code={exp.fast.code} label="速い書き方のコード" /></div>
        <div class="actions">
          <button
            type="button"
            class="btn btn-primary"
            disabled={running !== null}
            aria-busy={running === 'fast'}
            data-testid="measure-fast"
            onclick={() => measure('fast')}
          >
            {#if running === 'fast'}計測中{:else}{st.fast
                ? '速い書き方をもう一度測る'
                : '速い書き方を測る'}
              {#if st.fast === null}<kbd>⌘ Enter</kbd>{/if}{/if}
          </button>
        </div>
      </section>
    {/if}

    {#if series.length > 0}
      <section class="step" aria-labelledby="{uid}-res" data-testid="compare-results">
        <h3 id="{uid}-res">実測</h3>
        <MeterResults {series} measuring={running !== null} separate={series.length === 2} />
        {#if agreement}
          <p class="small agree {agreement.ok ? 'match' : 'gap'}">
            <StateIcon kind={agreement.ok ? 'ok' : 'diff'} /><span>{agreement.text}</span>
          </p>
        {/if}
      </section>
    {/if}
  </div>

  <section class="log" aria-labelledby="{uid}-log" data-testid="deviation-log">
    <h3 id="{uid}-log">予想と実測の記録</h3>
    {#if log.length === 0}
      <p class="text-soft small">遅い書き方を測ると、予想と実測をここに並べます。</p>
    {:else}
      <table class="log-table">
        <caption class="visually-hidden">実験ごとの予想と実測</caption>
        <thead>
          <tr
            ><th scope="col">実験</th><th scope="col">予想</th><th scope="col">実測</th><th
              scope="col">結果</th
            ></tr
          >
        </thead>
        <tbody>
          {#each log as d (d.experimentId)}
            <tr>
              <th scope="row">{d.title}</th>
              <td><Pow text={d.declared.label} /></td>
              <td><Pow text={d.measured.label} /></td>
              <td class={d.match ? 'match' : 'gap'}>
                <span class="cell-flag"
                  ><StateIcon kind={d.match ? 'ok' : 'diff'} />{d.match ? '一致' : '違った'}</span
                >
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
      <p class="text-soft small">この記録はページを閉じると消えます。</p>
    {/if}
  </section>
  <p class="visually-hidden" aria-live="polite">
    {running ? `${variantName[running]}を測っています。` : ''}
  </p>
</div>

<style>
  .compare {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .panel-body {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding-top: var(--space-3);
  }
  .step {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  h3 {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--fs-md);
    margin: 0;
  }
  .step-n {
    display: inline-grid;
    place-items: center;
    width: 1.6rem;
    height: 1.6rem;
    border-radius: var(--radius-pill);
    background: var(--canvas);
    font-size: var(--fs-sm);
    font-variant-numeric: tabular-nums;
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
  }
  legend {
    margin-bottom: var(--space-2);
  }
  .choices {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .choice {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-height: var(--tap-min);
    padding: 0 var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    cursor: pointer;
  }
  .choice:has(input:checked) {
    border-color: var(--text);
    font-weight: var(--fw-semibold);
  }
  fieldset:disabled .choice {
    cursor: default;
  }
  .code-scroll {
    max-height: 22rem;
    overflow: auto;
    border-radius: var(--radius-md);
  }
  .actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-3);
  }
  .small {
    font-size: var(--fs-sm);
  }
  .status {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: 0;
  }
  .err {
    color: var(--err-text);
  }
  .diag {
    max-height: 12rem;
    overflow: auto;
  }
  .dev,
  .agree,
  .cell-flag {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-weight: var(--fw-semibold);
  }
  .cell-flag {
    display: inline-flex;
  }
  .match {
    color: var(--ok-text);
  }
  .gap {
    color: var(--warn-text);
  }
  .log {
    border-top: 1px solid var(--border);
    padding-top: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .log-table {
    width: 100%;
  }
  .log-table th,
  .log-table td {
    text-align: left;
    padding: var(--space-1) var(--space-3);
    border-bottom: 1px solid var(--border);
    font-size: var(--fs-sm);
  }
  .log-table thead th {
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
  }
</style>
